import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline"
import { useMutation } from "@tanstack/react-query"
import { Form, Input, Modal } from "antd"
import { Button, H7, P2 } from "components"
import PersonAvatar from "components/new-stuff/ui/PersonAvatar"
import { useEffect, useState } from "react"
import { authService } from "services/authService"
import { useAuthStore } from "stores"
import { onQueryError } from "utils/utils"

type Props = {
  onClose: (status?: boolean) => void
  onSetupToken?: (setupToken: string) => void
}

export const VerifyPasswordPrompt = ({ onClose, onSetupToken }: Props) => {
  const [form] = Form.useForm()
  const { user } = useAuthStore()
  const isEmployee = location.pathname.includes("/employee")
  const [showPassword, setShowPassword] = useState<boolean>(false)

  const forSetup = !!onSetupToken

  useEffect(() => {
    return () => form.setFieldValue("password", "")
  }, [])

  const { isLoading: isLoadingConfirmPassword, mutate: submit } = useMutation({
    mutationFn: (password: string) =>
      forSetup
        ? authService.passwordAuthenticate2FA({
            username: user?.userName ?? "",
            password,
          })
        : isEmployee
        ? authService.verifyEmployeePassword({ password })
        : authService.verifyAdminPassword({ password }),
    onSuccess: (data: any) => {
      form.resetFields()
      form.setFieldValue("password", "")
      if (forSetup) onSetupToken!(data?.data?.access_token ?? "")
      onClose(true)
    },
    onError: onQueryError,
  })

  return (
    <Modal
      open
      footer={null}
      onCancel={() => onClose()}
      title={
        <div>
          <H7 className="text-neutral500 font-bold">
            To continue, please confirm you're {user?.userFirstName}
          </H7>
        </div>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={(values) => {
          submit(values?.password)
        }}
      >
        <div className="px-8 md:px-16 py-8 h-full w-full flex flex-col gap-6 justify-center items-center">
          <P2 className="font-circular text-neutral400 font-light text-center"></P2>

          <div className="w-full flex flex-col gap-2 justify-center items-center">
            <PersonAvatar
              className="object-cover rounded-full w-[70px] h-[70px] border-4"
              src={user?.userAvatarFileUrl}
              alt=""
            />
            <H7 className="font-medium text-neutral700">{user?.userName}</H7>
          </div>
        </div>
        <Form.Item
          name={"password"}
          rules={[
            {
              required: true,
              message: "Please enter your password",
            },
          ]}
          initialValue={""}
          label={
            <span className="font-avenir text-sm text-neutral400">
              Password
            </span>
          }
        >
          <Input
            type={showPassword ? "text" : "password"}
            suffix={
              <>
                {showPassword === true ? (
                  <EyeSlashIcon
                    onClick={() => setShowPassword(false)}
                    className="w-5 text-neutral80"
                  />
                ) : (
                  <EyeIcon
                    onClick={() => setShowPassword(true)}
                    className="w-5 text-neutral80"
                  />
                )}
              </>
            }
          />
        </Form.Item>
      </Form>
      <div className="mt-3 w-full flex justify-end items-center gap-4">
        <Button
          color="secondary"
          title="Confirm"
          onClick={() => {
            onClose()
          }}
          type="button"
        >
          Cancel
        </Button>
        <Button
          color="primary"
          title="Confirm"
          onClick={(e) => {
            e.preventDefault()
            form.submit()
          }}
          submitting={isLoadingConfirmPassword}
        >
          Confirm
        </Button>
      </div>
    </Modal>
  )
}
